import React from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatIDR, formatDate, INVOICE_STATUS } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function InvoicesPage() {
  const [rows, setRows] = React.useState([]);
  const [selected, setSelected] = React.useState([]);
  const load = React.useCallback(async () => { const { data } = await api.get("/invoices"); setRows(data); setSelected([]); }, []);
  React.useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const toggleAll = () => setSelected(selected.length === rows.length ? [] : rows.map((r) => r.id));

  const bulkDelete = async () => {
    try { const { data } = await api.post("/invoices/bulk-delete", { ids: selected }); toast.success(`${data.deleted} invoice dibatalkan`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>Invoice</h1>
          <p className="text-sm text-neutral-500">Semua tagihan ke pelanggan.</p>
        </div>
        {selected.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="bulk-delete-invoices">
                Hapus {selected.length} Invoice
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Batalkan {selected.length} invoice?</AlertDialogTitle>
                <AlertDialogDescription>
                  Invoice yang dipilih akan diubah statusnya menjadi <b>Dibatalkan</b>. Data historis tetap terjaga (soft delete).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Batal</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-bulk-delete-invoices">Ya, Batalkan</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="w-full data-table">
          <thead><tr>
            <th className="w-8"><input type="checkbox" checked={selected.length === rows.length && rows.length > 0} onChange={toggleAll} data-testid="select-all-invoices" /></th>
            <th className="text-left">No. Invoice</th>
            <th className="text-left">Pelanggan</th>
            <th className="text-left">Tanggal</th>
            <th className="text-left">Jatuh Tempo</th>
            <th className="text-right">Total</th>
            <th className="text-right">Dibayar</th>
            <th className="text-right">Sisa</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr></thead>
          <tbody>
            {rows.map((iv) => (
              <tr key={iv.id} data-testid={`inv-row-${iv.id}`}>
                <td><input type="checkbox" checked={selected.includes(iv.id)} onChange={() => toggle(iv.id)} data-testid={`select-inv-${iv.id}`} /></td>
                <td className="font-mono text-xs">{iv.number}</td>
                <td data-testid={`inv-customer-${iv.id}`}>{iv.customer_name || "-"}</td>
                <td className="text-xs">{formatDate(iv.issue_date)}</td>
                <td className="text-xs">{formatDate(iv.due_date)}</td>
                <td className="text-right">{formatIDR(iv.total)}</td>
                <td className="text-right text-green-700">{formatIDR(iv.amount_paid)}</td>
                <td className="text-right text-red-700">{formatIDR(iv.outstanding)}</td>
                <td><StatusBadge statusMap={INVOICE_STATUS} value={iv.status} /></td>
                <td className="text-right"><Link to={`/invoices/${iv.id}`} className="text-green-700 text-xs hover:underline">Detail →</Link></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-sm text-neutral-500">Belum ada invoice.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
